import { getCase, listCases, removeCaseFile } from '@/backend/case'
import { getProject } from '@/backend/getProject'
import type { APIResult, ProjectResponse } from '@/backend/types'
import { useEntityClient } from '@/data/useEntityClient'
import { useEntityStore } from '@/data/useEntityStore'
import type { Entity } from '@/modules/Project/types'
import { serializeProject, type Project } from '@/modules/Projects/useProjects'
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { serializeCase } from './serializers'
import { isFullCase, type Case, type CaseFile, type CaseOutput, type CaseResponse } from './types'

type CaseOutputWithAssociatedData = CaseOutput & {
  project: Project & { name: string }
  entity: Entity
}

type CaseFilesCategorized = {
  /**
   * Files attached to a message, mapped by message id
   */
  attached: Partial<Record<string, CaseFile[]>>
  /**
   * Files not attached to a message, and not in an error or uploading state.
   * These could be done or are still processing. We can't currently delete them,
   * so we categorize them separately in order to not show them above message input.
   */
  detached: CaseFile[]
  /**
   * Files not attached to a message, and in an error state.
   * This got uploaded, but failed to process.
   * They will be shown as errored above the message input and can be deleted.
   */
  detachedErrored: CaseFile[]
  /**
   * Files not attached to a message, and in an uploading state.
   * This are registered but haven't been uploaded yet. In a multi-user environment,
   * this could be another user's upload, but with cases, it's likely the current user
   * has just aborted the upload somehow, for example by closing the tab.
   * They will be shown as errored above the message input and can be deleted.
   */
  detachedUploading: CaseFile[]
}

/**
 * Private helper store for case store, holding loaded project and entity data.
 * If we switch to something like tanstack, we should aim to replace this with it.
 * We should NEVER make this public.
 */
const useCaseOutputDataStore = defineStore('caseOutputData', () => {
  const projects = ref<Partial<Record<string, { lastFetchedAt: number; project: Project }>>>({})
  return { projects }
})

/**
 * This store holds all state relating to cases, including:
 * - All cases in the current workspace
 * - The currently active case
 */
export const useCaseStore = defineStore('case', () => {
  const loadingState = ref<'idle' | 'loading' | 'loaded' | 'errored'>('idle')
  const hasMoreCases = ref(false)
  const currentCursor = ref<string | null>(null)
  const activeCaseId = ref<string | null>(null)
  const recentCases = ref<Case[]>([])
  const cases = ref<Case[]>([])
  /**
   * Holds the total number of cases in the workspace, as fetched from the backend.
   * 0 means not yet loaded or no cases.
   */
  const caseCount = ref<number>(0)
  /**
   * Tracks the loading state of the caseCount fetch operation.
   */
  const caseCountLoading = ref(false)

  const isLoading = computed(() => loadingState.value === 'loading')

  const activeCase = computed<Case | null>(
    () => cases.value.find((c) => c.id === activeCaseId.value) || null,
  )

  const outputStore = useCaseOutputDataStore()
  const entityClient = useEntityClient()
  const entityStore = useEntityStore()

  const activeCaseOutputs = computed<CaseOutputWithAssociatedData[] | null>(() => {
    if (!activeCase.value) return null

    const outputsWithData: CaseOutputWithAssociatedData[] = []

    for (const output of activeCase.value.outputs) {
      const agent = activeCaseAgentsById.value[output.projectId]
      const entity = entityStore.entitiesById[output.entityId]
      if (agent && entity) {
        outputsWithData.push({ ...output, project: agent, entity })
      }
    }

    return outputsWithData
  })

  const activeCaseAgentsById = computed<Record<string, Project & { name: string }>>(() => {
    if (!activeCase.value) return {}

    const agents: Record<string, Project & { name: string }> = {}

    for (const output of activeCase.value.outputs) {
      const project = outputStore.projects[output.projectId]?.project
      if (project) {
        agents[output.projectId] = {
          ...project,
          name: project.name || '',
        }
      }
    }

    return agents
  })

  const activeCaseHasOutputs = computed<boolean>(
    () => !!activeCaseOutputs.value && activeCaseOutputs.value.length > 0,
  )

  /**
   * Returns the most recent output of an active case
   */
  const latestOutput = computed<CaseOutputWithAssociatedData | undefined>(() => {
    if (!activeCase.value) {
      return undefined
    }

    // We get most recent base first, as activeCaseOutputs might
    // be null if data is not loaded yet, or might be missing unloaded outputs
    const latestBase = activeCase.value.outputs
      .toSorted((a, b) => b.createdAt.localeCompare(a.createdAt))
      .at(0)

    return latestBase && activeCaseOutputs.value?.find((o) => o.id === latestBase.id)
  })

  const getCaseById = (id: string): Case | undefined => cases.value.find((c) => c.id === id)

  type RevertFn = () => void

  const setCase = (c: Case): RevertFn => {
    const index = cases.value.findIndex(({ id }) => c.id === id)
    const recentCaseIndex = recentCases.value.findIndex(({ id }) => c.id === id)

    const oldCase = cases.value[index]
    const oldRecentCase = recentCases.value[recentCaseIndex]

    if (index === -1) {
      cases.value.unshift(c)
    } else {
      cases.value[index] = { ...cases.value[index], ...c }
    }

    if (recentCaseIndex === -1) {
      recentCases.value.unshift(c)
    } else {
      recentCases.value[recentCaseIndex] = { ...recentCases.value[recentCaseIndex], ...c }
    }

    return () => {
      if (index === -1) {
        cases.value.shift()
      } else {
        invariant(oldCase)
        cases.value[index] = oldCase
      }

      if (recentCaseIndex === -1) {
        recentCases.value.shift()
      } else {
        invariant(oldRecentCase)
        recentCases.value[recentCaseIndex] = oldRecentCase
      }
    }
  }

  const pendingProjectLoads: Partial<Record<string, Promise<APIResult<ProjectResponse>>>> = {}

  const ONE_MINUTE_MS = 60 * 1000

  const isStale = (r: { lastFetchedAt: number }): boolean =>
    r.lastFetchedAt + ONE_MINUTE_MS < Date.now()

  const loadCaseOutputData = async (workspaceId: string, caseSubject: Case, since?: string) => {
    const projectPromises: Promise<APIResult<ProjectResponse>>[] = []
    const entityPromises: Promise<unknown>[] = []

    const outputs = since
      ? caseSubject.outputs.filter((o) => o.createdAt > since)
      : caseSubject.outputs

    outputs.forEach((output) => {
      const project = outputStore.projects[output.projectId]

      if (!project || isStale(project)) {
        const promise =
          pendingProjectLoads[output.projectId] || getProject(workspaceId, output.projectId)
        pendingProjectLoads[output.projectId] = promise
        projectPromises.push(promise)
      }

      entityPromises.push(
        entityClient.fetchEntity({
          workspaceId,
          projectId: output.projectId,
          entityId: output.entityId,
          staleTimeMs: 500,
        }),
      )
    })

    const projectResponses = await Promise.all(projectPromises)
    projectResponses.forEach((response) => {
      if (response.ok) {
        pendingProjectLoads[response.data.id] = undefined
        outputStore.projects[response.data.id] = {
          project: serializeProject(response.data),
          lastFetchedAt: Date.now(),
        }
      }
    })

    await Promise.all(entityPromises)
  }

  const setActiveCase = async (payload: {
    workspaceId: string
    caseId: string
  }): Promise<{ ok: boolean }> => {
    const { workspaceId, caseId } = payload

    activeCaseId.value = caseId

    const res = await getCase({ workspaceId, caseId })

    if (!res.ok) {
      activeCaseId.value = null

      // naive interpretation of 400 as case not found, as backend will return it if we send a malformed case id
      if (![404, 400].includes(res.error.status ?? 0)) {
        toast.error('Failed to load case, contact support if this error persists.')
      }

      return { ok: false }
    }
    const caseResponse = res.data

    const newCase = serializeCase(caseResponse)
    setCase(newCase)

    loadCaseOutputData(workspaceId, newCase)

    return { ok: true }
  }

  const updateActiveCase = async ({
    workspaceId,
    caseResponse,
  }: {
    workspaceId: string
    caseResponse: CaseResponse
  }) => {
    if (activeCase.value?.id !== caseResponse.id) return
    const newCase = serializeCase(caseResponse)

    const lastQuery = newCase.messages
      .filter((m) => m.type === 'query')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .at(0)
    loadCaseOutputData(workspaceId, newCase, lastQuery?.createdAt)

    setCase(newCase)
  }

  const setCases = (value: Case[]) => {
    const oldCases = [...cases.value]

    cases.value = value

    return () => {
      cases.value = oldCases
    }
  }

  const addCases = (value: Case[]) => {
    const oldCases = [...cases.value]
    const uniqueCases = value.filter(
      (newCase) => !cases.value.some((existingCase) => existingCase.id === newCase.id),
    )

    cases.value = [...cases.value, ...uniqueCases]

    return () => {
      cases.value = oldCases
    }
  }

  const addOrUpdateMessageToActiveCase = (message: Case['messages'][number]): RevertFn => {
    const oldActiveCase = activeCase.value
    const index = cases.value.findIndex(({ id }) => activeCaseId.value === id)
    if (index === -1) return () => {}
    if (!isFullCase(cases.value[index])) return () => {}

    const existingMessageIndex = cases.value[index].messages.findIndex((m) => m.id === message.id)
    const caseWithNewMessage = {
      ...cases.value[index],
      messages:
        existingMessageIndex >= 0
          ? cases.value[index].messages.map((m, i) => (i === existingMessageIndex ? message : m))
          : [...cases.value[index].messages, message],
    }
    cases.value[index] = caseWithNewMessage

    return () => {
      if (!oldActiveCase) return
      cases.value[index] = oldActiveCase
    }
  }

  const removeCase = (id: string): RevertFn => {
    const oldCaseIndex = cases.value.findIndex((c) => c.id === id)
    const oldRecentCaseIndex = recentCases.value.findIndex((c) => c.id === id)
    const wasActiveCase = activeCaseId.value === id

    const oldCase = cases.value[oldCaseIndex]
    const oldRecentCase = recentCases.value[oldRecentCaseIndex]
    const oldActiveCaseId = activeCaseId.value

    if (oldCaseIndex !== -1) {
      cases.value.splice(oldCaseIndex, 1)
    }

    if (oldRecentCaseIndex > -1) {
      recentCases.value.splice(oldRecentCaseIndex, 1)
    }

    if (wasActiveCase) {
      activeCaseId.value = null
    }

    return () => {
      if (oldCaseIndex > -1) {
        invariant(oldCase)
        cases.value.splice(oldCaseIndex, 0, oldCase)
      }

      if (oldRecentCaseIndex > -1) {
        invariant(oldRecentCase)
        recentCases.value.splice(oldRecentCaseIndex, 0, oldRecentCase)
      }

      if (wasActiveCase) {
        activeCaseId.value = oldActiveCaseId
      }
    }
  }

  const $reset = (): void => {
    cases.value = []
    currentCursor.value = null
    activeCaseId.value = null
    hasMoreCases.value = false
  }

  const setLoadingState = (state: 'idle' | 'loading' | 'loaded' | 'errored'): void => {
    loadingState.value = state
  }

  const activeCaseFilesCategorized = computed<CaseFilesCategorized>(() => {
    if (!isFullCase(activeCase.value)) {
      return {
        attached: {},
        detached: [],
        detachedErrored: [],
        detachedUploading: [],
      }
    }

    const files = activeCase.value.files || []
    const messages = activeCase.value?.messages ?? []

    const filesByMessage: Partial<Record<string, CaseFile[]>> = {}

    const attachedFileIds = new Set<string>()

    messages.forEach((message) => {
      if (message.type !== 'query') return
      const messageFiles: CaseFile[] = []

      message.fileIds.forEach((fileId) => {
        attachedFileIds.add(fileId)
        const file = files.find((f) => f.id === fileId)
        if (!file) return
        messageFiles.push(file)
      })
      filesByMessage[message.id] = messageFiles
    })

    return {
      attached: filesByMessage,
      detached: files.filter(
        (f) => !attachedFileIds.has(f.id) && f.status !== 'error' && f.status !== 'uploading',
      ),
      detachedErrored: files.filter((f) => !attachedFileIds.has(f.id) && f.status === 'error'),
      detachedUploading: files.filter(
        (f) => !attachedFileIds.has(f.id) && f.status === 'uploading',
      ),
    }
  })

  const deleteActiveCaseFile = async (workspaceId: string, fileId: string): Promise<void> => {
    invariant(activeCase.value, 'Active case is required')
    const files = isFullCase(activeCase.value) ? activeCase.value.files : []
    const index = files.findIndex((f) => f.id === fileId)
    if (index === -1) return
    const file = files[index]
    if (!file) return
    const res = await removeCaseFile(workspaceId, activeCase.value.id, fileId)
    if (res.ok) {
      files.splice(index, 1)
    }
  }

  /**
   * Updates the caseCount and caseCountLoading refs by fetching the case count directly from the backend.
   * This is the only place that updates these refs.
   * @param workspaceId string - the workspace to fetch the case count for
   */
  async function updateCaseCount(workspaceId: string, searchValue: string) {
    caseCountLoading.value = true
    try {
      const res = await listCases({ workspaceId, offset: 1, limit: 1, name_contains: searchValue })
      if (res.ok) {
        caseCount.value = res.data.metadata.total_count ?? 0
      }
    } catch {
      caseCount.value = 0
    }
    caseCountLoading.value = false
  }

  return {
    activeCase,
    activeCaseFilesCategorized,
    activeCaseHasOutputs,
    activeCaseId,
    activeCaseOutputs,
    activeCaseAgentsById,
    addOrUpdateMessageToActiveCase,
    deleteActiveCaseFile,
    setActiveCase,
    updateActiveCase,
    addCases,
    cases,
    getCaseById,
    hasMoreCases,
    isLoading,
    latestOutput,
    loadingState,
    recentCases,
    removeCase,
    setCase,
    setCases,
    setLoadingState,

    $reset,
    caseCount,
    caseCountLoading,
    updateCaseCount,
  }
})
